import {FC, useState, useRef, useEffect, useCallback} from 'react'
import {createPortal} from 'react-dom'
import {IssueStatus, WaitingState, IssueUrgency, IssueAttachment} from './_models'

// ---- formatting helpers ----
export function timeAgo(dateStr: string): string {
  if (!dateStr) return ''
  const sec = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (sec < 60) return 'לפני רגע'
  const min = Math.floor(sec / 60)
  if (min < 60) return `לפני ${min} דק'`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `לפני ${hr} שע'`
  const days = Math.floor(hr / 24)
  if (days < 30) return `לפני ${days} ימים`
  const months = Math.floor(days / 30)
  if (months < 12) return `לפני ${months} חוד'`
  return `לפני ${Math.floor(months / 12)} שנים`
}

export function fullDate(dateStr: string): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const isImage = (a: IssueAttachment) =>
  /^image\//.test(a.type) || /\.(png|jpe?g|gif|webp)$/i.test(a.url)

// ---- status / waiting metadata ----
export const STATUS_META: Record<IssueStatus, {label: string; color: string; bg: string}> = {
  open: {label: 'פתוח', color: '#f1416c', bg: '#fff1f4'},
  in_progress: {label: 'בטיפול', color: '#d98a00', bg: '#fff7e8'},
  done: {label: 'הושלם', color: '#17a865', bg: '#eafaf0'},
}
export const STATUS_ORDER: IssueStatus[] = ['open', 'in_progress', 'done']

export const WAITING_META: Record<Exclude<WaitingState, 'none'>, {label: string; color: string; bg: string}> = {
  aryeh: {label: 'ממתין לאריה', color: '#3e97ff', bg: '#eef6ff'},
  support: {label: 'ממתין לתמיכה', color: '#f6a609', bg: '#fff7e8'},
}

// ---- urgency metadata (gray / orange / red traffic-light) ----
export const URGENCY_META: Record<IssueUrgency, {label: string; color: string; bg: string}> = {
  low: {label: 'נמוך', color: '#4a9d6a', bg: '#eaf7ef'},
  normal: {label: 'רגיל', color: '#8a92a6', bg: '#f1f2f5'},
  high: {label: 'גבוה', color: '#d98a00', bg: '#fff7e8'},
  urgent: {label: 'דחוף', color: '#f1416c', bg: '#fff1f4'},
}
export const URGENCY_ORDER: IssueUrgency[] = ['low', 'normal', 'high', 'urgent']

// Compact urgency dropdown — colored by the current level, usable inside the
// collapsed header (stops click-to-expand) or anywhere else.
export const UrgencySelect: FC<{value: IssueUrgency; onChange: (u: IssueUrgency) => void}> = ({value, onChange}) => {
  const m = URGENCY_META[value]
  return (
    <select
      value={value}
      title='דחיפות'
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation()
        onChange(e.target.value as IssueUrgency)
      }}
      style={{
        border: 'none',
        borderRadius: 999,
        padding: '4px 8px',
        fontSize: 12,
        fontWeight: 700,
        background: m.bg,
        color: m.color,
        cursor: 'pointer',
        appearance: 'none',
        textAlignLast: 'center',
      }}
    >
      {URGENCY_ORDER.map((u) => (
        <option key={u} value={u} style={{background: '#fff', color: '#1b1f2a'}}>
          {URGENCY_META[u].label}
        </option>
      ))}
    </select>
  )
}

// ---- avatar ----
const AVATAR_COLORS = ['#5b6ef5', '#17a865', '#f6a609', '#e0518a', '#7a5af5', '#0bb5c9', '#ef6c4d']
export function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < (seed || '').length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}
export function initials(name: string): string {
  const s = (name || '').trim()
  if (!s) return '?'
  const parts = s.split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export const Avatar: FC<{name: string; size?: number}> = ({name, size = 28}) => (
  <span
    className='iss-avatar'
    style={{width: size, height: size, background: avatarColor(name), fontSize: size * 0.4}}
    title={name}
  >
    {initials(name)}
  </span>
)

// ---- status pill ----
export const StatusPill: FC<{status: IssueStatus}> = ({status}) => {
  const m = STATUS_META[status]
  return (
    <span className='iss-chip' style={{background: m.bg, color: m.color}}>
      <span className='iss-dot' style={{background: m.color}}></span>
      {m.label}
    </span>
  )
}

export const WaitingPill: FC<{waiting: WaitingState}> = ({waiting}) => {
  if (waiting === 'none') return null
  const m = WAITING_META[waiting]
  return (
    <span className='iss-chip' style={{background: m.bg, color: m.color}}>
      <i className='bi bi-hourglass-split'></i>
      {m.label}
    </span>
  )
}

// ---- media dropzone (click OR drag-and-drop) ----
// The parent owns the upload + state; this just surfaces files and previews.
export const MediaDropzone: FC<{
  attachments: IssueAttachment[]
  uploading: boolean
  onPick: (files: FileList) => void
  onRemove?: (idx: number) => void
}> = ({attachments, uploading, onPick, onRemove}) => {
  const [drag, setDrag] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div>
      <div
        className='iss-dropzone'
        data-drag={drag ? '1' : '0'}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          if (!drag) setDrag(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setDrag(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setDrag(false)
          if (e.dataTransfer.files && e.dataTransfer.files.length) onPick(e.dataTransfer.files)
        }}
      >
        <i className={`bi bi-${uploading ? 'arrow-repeat' : 'cloud-arrow-up'}`} style={{fontSize: 20, color: '#8a92a6'}}></i>
        <div style={{fontSize: 12, color: '#8a92a6'}}>
          {uploading ? 'מעלה...' : 'גרור לכאן קבצים או לחץ לבחירה'}
        </div>
        <input
          ref={inputRef}
          type='file'
          multiple
          accept='image/*,video/*,.pdf'
          hidden
          onChange={(e) => {
            if (e.target.files && e.target.files.length) onPick(e.target.files)
            e.target.value = ''
          }}
        />
      </div>
      {attachments.length > 0 && (
        <div className='d-flex flex-wrap gap-2 mt-2'>
          {attachments.map((a, idx) => (
            <div key={idx} style={{position: 'relative'}}>
              {isImage(a) ? (
                <img src={a.url} alt={a.name} className='iss-thumb' style={{width: 56, height: 56}} />
              ) : (
                <span
                  className='iss-chip'
                  style={{background: '#eef0f5', color: '#4a5163', height: 56, borderRadius: 10}}
                >
                  <i className='bi bi-paperclip'></i>
                  {(a.name || 'קובץ').slice(0, 14)}
                </span>
              )}
              {onRemove && (
                <span
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove(idx)
                  }}
                  style={{
                    position: 'absolute',
                    top: -6,
                    insetInlineEnd: -6,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: '#1b1f2a',
                    color: '#fff',
                    fontSize: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <i className='bi bi-x'></i>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Render text with any URLs turned into clickable links.
const URL_RE = /(https?:\/\/[^\s]+)/g
export const Linkify: FC<{text: string}> = ({text}) => {
  if (!text) return null
  const parts = text.split(URL_RE)
  return (
    <>
      {parts.map((p, i) =>
        /^https?:\/\//.test(p) ? (
          <a key={i} href={p} target='_blank' rel='noreferrer' style={{color: '#5b6ef5', wordBreak: 'break-all'}}>
            {p}
          </a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  )
}

// ---- lightbox gallery ----
const Lightbox: FC<{images: IssueAttachment[]; index: number; onClose: () => void; onIndex: (i: number) => void}> = ({
  images,
  index,
  onClose,
  onIndex,
}) => {
  const prev = useCallback(() => onIndex((index - 1 + images.length) % images.length), [index, images.length, onIndex])
  const next = useCallback(() => onIndex((index + 1) % images.length), [index, images.length, onIndex])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') next()
      else if (e.key === 'ArrowRight') prev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, next, prev])

  const img = images[index]
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,11,18,0.88)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <button className='iss-lb-btn' style={{top: 18, insetInlineEnd: 18, transform: 'none'}} onClick={onClose}>
        <i className='bi bi-x-lg'></i>
      </button>
      {images.length > 1 && (
        <>
          <button
            className='iss-lb-btn'
            style={{insetInlineStart: 18, top: '50%'}}
            onClick={(e) => {
              e.stopPropagation()
              prev()
            }}
          >
            <i className='bi bi-chevron-left'></i>
          </button>
          <button
            className='iss-lb-btn'
            style={{insetInlineEnd: 18, top: '50%'}}
            onClick={(e) => {
              e.stopPropagation()
              next()
            }}
          >
            <i className='bi bi-chevron-right'></i>
          </button>
        </>
      )}
      <img
        src={img.url}
        alt={img.name}
        onClick={(e) => e.stopPropagation()}
        style={{maxWidth: '90vw', maxHeight: '86vh', borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,.5)'}}
      />
      {images.length > 1 && (
        <div style={{position: 'fixed', bottom: 22, color: '#fff', fontSize: 13, opacity: 0.85}}>
          {index + 1} / {images.length}
        </div>
      )}
    </div>,
    document.body
  )
}

// ---- attachments (images open in a lightbox gallery; other files link out) ----
export const AttachmentList: FC<{attachments: IssueAttachment[]; size?: number}> = ({attachments, size = 60}) => {
  const [lb, setLb] = useState<number | null>(null)
  if (!attachments || !attachments.length) return null
  const images = attachments.filter(isImage)
  const files = attachments.filter((a) => !isImage(a))
  return (
    <div className='d-flex flex-wrap gap-2'>
      {images.map((a, idx) => (
        <img
          key={idx}
          src={a.url}
          alt={a.name}
          className='iss-thumb'
          style={{width: size, height: size, cursor: 'pointer'}}
          onClick={() => setLb(idx)}
        />
      ))}
      {files.map((a, idx) => (
        <a
          key={idx}
          href={a.url}
          target='_blank'
          rel='noreferrer'
          className='iss-chip'
          style={{background: '#eef0f5', color: '#4a5163', textDecoration: 'none'}}
        >
          <i className='bi bi-paperclip'></i>
          {a.name || 'קובץ'}
        </a>
      ))}
      {lb !== null && <Lightbox images={images} index={lb} onClose={() => setLb(null)} onIndex={setLb} />}
    </div>
  )
}
